"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import react, { useCallback, useMemo, useRef, useState } from "react";
import { Drawer } from "vaul";
import { PortalContainerProvider } from "../../contexts/index.js";
import { cn } from "../../utils/index.js";
import { CdnIcon } from "../CdnIcon/index.js";
import { Heading } from "../Heading/index.js";
import { IconButton } from "../IconButton/index.js";
import { ScrollArea } from "../ScrollArea/index.js";
import { Span } from "../Text/index.js";
import { drawerVariants } from "./Drawer.variants.js";
const visuallyHiddenClass = 'sr-only';
function Drawer_Drawer({ children, description, footer, headingCustomNode, headingIconName, headingRightNode, headingText, open, defaultOpen, showCloseButton = true, showDragHandle = true, title, triggerNode, classNames, onNaturalClose, onOpenChange, ...props }) {
    const slots = useMemo(()=>drawerVariants({
            showDragHandle,
            customBody: children ? 'string' != typeof children : false,
            customFooter: !!footer,
            customHeader: !!headingCustomNode,
            showCloseButton,
            useSnapping: !!props.snapPoints,
            size: 'bottom' !== props.direction && void 0 !== props.direction ? 'sm' : props.size,
            hasHeadingText: !!headingText,
            direction: props.direction,
            scroll: props.scroll,
            stickyFooter: props.stickyFooter
        }), [
        showDragHandle,
        children,
        footer,
        headingCustomNode,
        showCloseButton,
        props.snapPoints,
        props.direction,
        props.size,
        props.scroll,
        props.stickyFooter,
        headingText
    ]);
    const { overlaySlot, headingSlot, headingSlotLeft, headingRightNodeSlot, closeButtonSlot, headingTextSlot, headingIconSlot, bodySlot, scrollAreaSlot, contentSlot, handlePositionSlot, handleSlot, footerSlot } = slots;
    const contentSlotClass = cn(contentSlot(), classNames?.contentSlot);
    const headingSlotClass = cn(headingSlot(), classNames?.headingSlot);
    const bodySlotClass = cn(bodySlot(), classNames?.bodySlot);
    const scrollAreaSlotClass = cn(scrollAreaSlot(), classNames?.scrollAreaSlot);
    const [snap, setSnap] = useState(props.snapPoints?.[0] || null);
    const portalContainerRef = useRef(null);
    const [, forceUpdate] = react.useReducer((x)=>x + 1, 0);
    const setPortalContainerRef = useCallback((el)=>{
        if (portalContainerRef.current !== el) {
            portalContainerRef.current = el;
            if (el) forceUpdate();
        }
    }, []);
    const handleOpenChange = useCallback((isOpen)=>{
        if (!isOpen) onNaturalClose?.();
        onOpenChange?.(isOpen);
    }, [
        onNaturalClose,
        onOpenChange
    ]);
    const handleClose = useCallback(()=>{
        onNaturalClose?.();
        onOpenChange?.(false);
    }, [
        onNaturalClose,
        onOpenChange
    ]);
    const snapProps = useMemo(()=>props.snapPoints ? {
            activeSnapPoint: snap,
            setActiveSnapPoint: setSnap
        } : {}, [
        props.snapPoints,
        snap
    ]);
    const hasDescription = description || !children;
    const contentAriaProps = hasDescription ? {} : {
        'aria-describedby': void 0
    };
    return /*#__PURE__*/ jsxs(Drawer.Root, {
        ...props,
        open: open,
        onOpenChange: handleOpenChange,
        defaultOpen: void 0 === open ? defaultOpen : false,
        ...snapProps,
        children: [
            /*#__PURE__*/ jsx(Drawer.Trigger, {
                asChild: true,
                children: triggerNode
            }),
            /*#__PURE__*/ jsxs(Drawer.Portal, {
                children: [
                    /*#__PURE__*/ jsx(Drawer.Overlay, {
                        className: overlaySlot()
                    }),
                    /*#__PURE__*/ jsxs(Drawer.Content, {
                        className: contentSlotClass,
                        ...contentAriaProps,
                        children: [
                            description && /*#__PURE__*/ jsx(Drawer.Description, {
                                className: visuallyHiddenClass,
                                children: description
                            }),
                            !description && !children && /*#__PURE__*/ jsx(Drawer.Description, {
                                className: visuallyHiddenClass,
                                children: "No content available"
                            }),
                            showDragHandle && /*#__PURE__*/ jsx("div", {
                                className: handlePositionSlot(),
                                children: /*#__PURE__*/ jsx(Drawer.Handle, {
                                    className: handleSlot()
                                })
                            }),
                            /*#__PURE__*/ jsxs("div", {
                                className: headingSlotClass,
                                children: [
                                    headingCustomNode && /*#__PURE__*/ jsx(Drawer.Title, {
                                        className: "w-full",
                                        children: headingCustomNode
                                    }),
                                    !headingCustomNode && headingText && /*#__PURE__*/ jsxs(Fragment, {
                                        children: [
                                            /*#__PURE__*/ jsxs("div", {
                                                className: headingSlotLeft(),
                                                children: [
                                                    headingIconName && /*#__PURE__*/ jsx(CdnIcon, {
                                                        iconName: headingIconName,
                                                        size: "24",
                                                        className: headingIconSlot()
                                                    }),
                                                    /*#__PURE__*/ jsx(Drawer.Title, {
                                                        asChild: true,
                                                        children: /*#__PURE__*/ jsx(Heading, {
                                                            as: "h4",
                                                            className: headingTextSlot(),
                                                            children: headingText
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ jsx("div", {
                                                className: headingRightNodeSlot(),
                                                children: headingRightNode
                                            })
                                        ]
                                    }),
                                    !headingCustomNode && !headingText && /*#__PURE__*/ jsxs(Fragment, {
                                        children: [
                                            /*#__PURE__*/ jsx("div", {
                                                className: "flex-1"
                                            }),
                                            /*#__PURE__*/ jsx(Drawer.Title, {
                                                asChild: true,
                                                children: /*#__PURE__*/ jsx("span", {
                                                    className: visuallyHiddenClass,
                                                    children: title
                                                })
                                            })
                                        ]
                                    }),
                                    showCloseButton && /*#__PURE__*/ jsx("div", {
                                        className: closeButtonSlot(),
                                        children: /*#__PURE__*/ jsx(Drawer.Close, {
                                            asChild: true,
                                            children: /*#__PURE__*/ jsx(IconButton, {
                                                iconName: "close",
                                                type: "primary",
                                                shadow: true,
                                                size: "md",
                                                "aria-label": "Close Drawer",
                                                emphasis: "bold",
                                                onClick: handleClose
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ jsx(ScrollArea, {
                                className: scrollAreaSlotClass,
                                drawerBody: true,
                                children: /*#__PURE__*/ jsx(PortalContainerProvider, {
                                    container: portalContainerRef.current,
                                    children: /*#__PURE__*/ jsx("div", {
                                        className: bodySlotClass,
                                        ref: setPortalContainerRef,
                                        children: 'string' == typeof children ? /*#__PURE__*/ jsx(Span, {
                                            textStyle: "body2",
                                            className: "px-4",
                                            children: children
                                        }) : children
                                    })
                                })
                            }),
                            footer && /*#__PURE__*/ jsx("div", {
                                className: footerSlot(),
                                children: footer
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
export { Drawer_Drawer as Drawer };
